/* eslint-disable no-unused-vars */
import FileAttachment from "./shared/FileAttachment";
import { useImpactedModules } from "../../hooks/useImpactedModules";
import styles from "../../styles/forms/SharedForm.module.css";
import SplitFormLayout from "../shared/SplitFormLayout";
import { safeLocalStorageGet } from "../../utils/safeJsonParse";
import { getSubmitterInfo, getDefaultCountry } from "../../utils/formHelpers";

import { useFormState } from "../../hooks/useFormState";
import { useFormSubmit } from "../../hooks/useFormSubmit";
import {
  FormSection,
  FormField,
  PinnedSubmit,
  SuccessCard,
} from "./shared/FormComponents";
import { FormProvider } from "./shared/FormContext";
import T, { useTranslate } from "../T";

const STEPS = [
  { id: "details", label: "Your Question", desc: "Summary & Details" },
  { id: "context", label: "Module & Context", desc: "Where & Who" },
  { id: "attachments", label: "Attachments", desc: "Relevant files" },
];

const INITIAL = {
  question: "",
  details: "",
  emailCCs: "",
  module: "",
  attachments: [],
};

export default function QuestionForm({ onBack }) {
  const { modules: impactedModules, isLoading: modulesLoading } = useImpactedModules();
  const { tText } = useTranslate();
  const {
    formData,
    handleChange,
    handleAddFiles,
    handleRemoveFile,
    setAllTouched,
    contextValue,
  } = useFormState(INITIAL);

  const { submitForm, isSubmitting, submitted, error } = useFormSubmit();

  const handleBack =
    onBack || (() => (window.location.href = "/staff/request/general"));

  const onSubmit = () => {
    const profile = safeLocalStorageGet("staff_profile");
    const { submittedBy, creatorName } = getSubmitterInfo(profile);
    const country = getDefaultCountry(profile);

    submitForm(formData, (attachments) => {
      const nocobasePayload = {
        user_name: creatorName,
        category: 1,
        Summary: formData.question,
        description: formData.details,
        impactedsystem: formData.module ? [formData.module] : [],
        priority: "Medium",
        environment: "Production",
        loanid: "",
        carid: "",
        dealerid: "",
        Description: formData.details,
        Country: country,
        sourceofrequest: "Staff Portal",
        confluencelink: "",
        accesstype: "",
        accesslevel: "",
        linemanagername: "",
        linemanageremail: "",
        mailinglist: "",
        status: "Open",
        tickettype: "General Inquiry/ System Enhancement",
        Resolution: "",
        rootcause: "",
        f_7ygfivdcnao: "",
        Creator: creatorName,
        Creatoremail: submittedBy,
        attachement: attachments.map(a => a.id),
      };

      return { nocobasePayload };
    });
  };

  if (submitted) {
    return (
      <SplitFormLayout
        badge="General Request"
        title="Question"
        subtitle="Ask operational or system-related questions."
        steps={STEPS.map(step => ({ ...step, label: tText(step.label), desc: tText(step.desc) }))}
        currentStep={STEPS.length}
        middleBreadcrumb={{ label: tText("General"), path: "/staff/request/general" }}
        currentBreadcrumb="Question"
      >
        <SuccessCard
          title="Question Submitted"
          description="Your question has been received! We will get back to you with an answer as soon as possible."
          onBack={handleBack}
          backLabel="Back to General"
        />
      </SplitFormLayout>
    );
  }

  const formValid = formData.question && formData.details;

  return (
    <SplitFormLayout
      badge="General Request"
      title="Question"
      subtitle="Ask operational or system-related questions."
      steps={STEPS.map(step => ({ ...step, label: tText(step.label), desc: tText(step.desc) }))}
      middleBreadcrumb={{ label: tText("General"), path: "/staff/request/general" }}
      currentBreadcrumb="Question"
    >
      <FormProvider value={contextValue}>
        <form
          onSubmit={(e) => e.preventDefault()}
          className={styles.ticketFormWrapper}
        >
          {error && <div className={styles.errorBanner}>{tText(error)}</div>}

          <FormSection id="details" title="1. Your Question">
            <FormField
              label="What is your question?"
              name="question"
              required
              style={{ marginBottom: "20px" }}
            >
              <input
                type="text"
                name="question"
                value={formData.question}
                onChange={handleChange}
                className={styles.input}
                placeholder={tText("Ask anything...")}
                required
              />
            </FormField>
            <FormField label="Tell us more details" name="details" required>
              <textarea
                name="details"
                value={formData.details}
                onChange={handleChange}
                className={styles.textarea}
                rows={6}
                placeholder={tText("Provide as much context as possible...")}
                required
              />
            </FormField>
          </FormSection>

          <FormSection id="context" title="2. Contextual Information">
            <FormField
              label="Modules"
              name="module"
              style={{ marginBottom: "20px" }}
            >
              <select
                name="module"
                value={formData.module}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="">{modulesLoading ? tText("Loading...") : tText("Select a module (optional)...")}</option>
                {impactedModules.map((m) => (
                  <option key={m} value={m}>
                    <T>{m}</T>
                  </option>
                ))}
              </select>
            </FormField>
          </FormSection>

          <FormSection id="attachments" title="3. Attachments">
            <FileAttachment
              attachments={formData.attachments}
              onChange={handleAddFiles}
              onRemove={handleRemoveFile}
            />
          </FormSection>

          <PinnedSubmit
            isSubmitting={isSubmitting}
            isValid={formValid}
            onSubmit={onSubmit}
            onInvalidSubmit={setAllTouched}
            label="Submit Question"
          />
        </form>
      </FormProvider>
    </SplitFormLayout>
  );
}
